import {createStyles, Stack, Text} from '@mantine/core';
import React from 'react';

import ArchiveModal from 'archive/components/modals/ArchiveModal';
import {SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';

interface SkillsListProps {
  skills: Array<SkillSummary>;
  isTenantAdmin: boolean;
  onArchive: (skillId: string, reason: string) => void;
  selectedSkillId?: string | null;
  onSelectSkill: (skill: SkillSummary) => void;
}

const useListStyles = createStyles(() => ({
  list: {
    overflowY: 'auto',
    flex: '1 1 auto',
    minHeight: 0,
  },
}));

const useCardStyles = createStyles((theme, {isSelected}: {isSelected: boolean}) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.sm} 10px`,
    borderRadius: theme.radius.sm,
    border: `1px solid ${isSelected ? theme.colors.blue[4] : theme.colors.gray[3]}`,
    backgroundColor: isSelected ? theme.colors.blue[0] : theme.white,
    cursor: 'pointer',
    ':hover': {
      borderColor: theme.colors.blue[3],
      backgroundColor: isSelected ? theme.colors.blue[0] : theme.colors.gray[0],
    },
  },
}));

function SkillCard({
  skill,
  isTenantAdmin,
  onArchiveClick,
  isSelected,
  onSelect,
}: {
  skill: SkillSummary;
  isTenantAdmin: boolean;
  onArchiveClick: (skill: SkillSummary) => void;
  isSelected: boolean;
  onSelect: (skill: SkillSummary) => void;
}): JSX.Element {
  // Store-installed skills are managed via the TAC extensions page (uninstall), not archived directly.
  const canArchive = skill.extensionDefinitionId == null && (skill.isOwnedByCurrentUser || isTenantAdmin);
  const {classes} = useCardStyles({isSelected});

  return (
    <div
      className={classes.card}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${skill.name}`}
      aria-pressed={isSelected}
      onClick={() => {
        onSelect(skill);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(skill);
        }
      }}
    >
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
            <Text fw="bold" size="sm" truncate={true}>
              {skill.name}
            </Text>
          </div>
          {skill.creatorName && (
            <Text size="xs" c="dimmed">
              by {skill.creatorName}
            </Text>
          )}
          <Text size="xs" c="dimmed" lineClamp={2} style={{marginTop: 2}}>
            {skill.description}
          </Text>
        </div>
        {canArchive && (
          <div style={{flexShrink: 0, marginTop: '2px'}}>
            <Button
              buttonStyle={ButtonStyle.TEXT}
              icon="archive"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onArchiveClick(skill);
              }}
              size={ButtonSize.SMALL}
              dataTestAttrs={{component: 'SkillsList', element: 'archiveSkillButton'}}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({title}: {title: string}): JSX.Element {
  return (
    <Text fw="bold" size="xs" c="dimmed" style={{textTransform: 'uppercase', letterSpacing: '0.5px'}}>
      {title}
    </Text>
  );
}

export default function SkillsList({
  skills,
  isTenantAdmin,
  onArchive,
  selectedSkillId,
  onSelectSkill,
}: SkillsListProps): JSX.Element {
  const {classes} = useListStyles();

  if (skills.length === 0) {
    return (
      <Text fw="normal" c="dimmed" size="sm">
        No skills available yet.
      </Text>
    );
  }

  const mySkills = skills.filter((s) => s.isOwnedByCurrentUser);
  const sharedWithMe = skills.filter((s) => !s.isOwnedByCurrentUser);

  function handleArchiveClick(skill: SkillSummary): void {
    legacyRunModal(ArchiveModal, {
      items: [{id: skill.id, name: skill.name}],
      onArchive: (itemIds: Array<string>, purpose: string) => {
        for (const id of itemIds) {
          onArchive(id, purpose);
        }
      },
    });
  }

  function handleSelectSkill(skill: SkillSummary): void {
    onSelectSkill(skill);
  }

  return (
    <Stack spacing="md" className={classes.list}>
      {mySkills.length > 0 && (
        <Stack spacing="xs">
          <SectionHeader title="My Skills" />
          {mySkills.map((skill) => (
            <SkillCard
              key={skill.id}
              skill={skill}
              isTenantAdmin={isTenantAdmin}
              onArchiveClick={handleArchiveClick}
              isSelected={selectedSkillId === skill.id}
              onSelect={handleSelectSkill}
            />
          ))}
        </Stack>
      )}
      {sharedWithMe.length > 0 && (
        <Stack spacing="xs">
          <SectionHeader title="Shared with Me" />
          {sharedWithMe.map((skill) => (
            <SkillCard
              key={skill.id}
              skill={skill}
              isTenantAdmin={isTenantAdmin}
              onArchiveClick={handleArchiveClick}
              isSelected={selectedSkillId === skill.id}
              onSelect={handleSelectSkill}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
